import React, { useState, useEffect } from "react";
import { X, ChevronDown, Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DeviceSchema, Device } from "@schemas";
import { addNewDevice, getAllSensorAgents } from "../../services/apiServices";
import { useModal } from "../../context/ModalContext";
import { useProjectContext } from "context/ProjectContext";

const AddNewDevice: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Device>({ resolver: zodResolver(DeviceSchema) });
  const { state } = useProjectContext();
  const [sensorAgents, setSensorAgents] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const { dispatch } = useModal();

  useEffect(() => {
    fetchSensorAgents();
  }, []);

  console.log(errors);

  // Function to fetch sensor agents
  const fetchSensorAgents = async () => {
    try {
      const fetchedData = await getAllSensorAgents(state.currentProjectID);
      console.log(fetchedData);
      setSensorAgents(fetchedData);
    } catch (err) {
      console.error(err);
    }
  };

  const addDevice = async (data: Device) => {
    try {
      const requestBody = { ...data, project_id: state.currentProjectID };
      console.log(requestBody);
      const response = await addNewDevice(requestBody);
      alert("Device added successfully!!");
      dispatch({ type: "CLOSE" });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="h-full">
      <div className="p-10 fixed inset-0 z-20 bg-gray-600 bg-opacity-50 overflow-hidden w-screen h-screen flex items-center justify-center">
        <div className="w-full p-2 bg-white rounded-lg flex flex-col h-full overflow-auto">
          <div className="flex justify-between items-center py-5 px-4">
            <h1 className="text-2xl font-bold text-custom-bluenew">
              Add New Device
            </h1>
            <button
              className="p-2 bg-custom-blue6 rounded-full"
              onClick={onClose}
            >
              <X className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4" />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit(addDevice)}>
            <div className="bg-custom-lightbg shadow-custom rounded-[10px] mx-5 pb-4">
              {/* Device id and name inputs*/}
              <div className="flex items-center relative">
                <div className="p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="deviceId"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device ID (MAC ID)<span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("device_id")}
                    type="text"
                    id="deviceId"
                    placeholder="Host"
                    className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                  />
                  {errors.device_id && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.device_id.message}
                    </p>
                  )}
                </div>
                <div className="p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="deviceName"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device Name
                  </label>
                  <input
                    {...register("device_name")}
                    type="text"
                    id="deviceName"
                    placeholder="Linux"
                    className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                  />
                </div>
              </div>

              {/* Connection protocol and ip address inputs*/}
              <div className="flex items-center">
                <div className="relative p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="connectionProtocol"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Connection Protocol<span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <select
                      {...register("connection_protocol")}
                      id="connectionProtocol"
                      className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                    >
                      <option value="">Select Connection Protocol</option>
                      <option value="SSH">SSH</option>
                      <option value="R Login">R Login</option>
                    </select>
                    <ChevronDown className="absolute right-[3px] bg-white text-custom-bluenew" />
                  </div>
                  {errors.connection_protocol && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.connection_protocol.message}
                    </p>
                  )}
                </div>
                <div className="p-[10px] space-y-[10px] w-1/2 relative">
                  <label
                    htmlFor="ipAddress"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    IP Address<span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("ip_address")}
                    type="text"
                    id="ipAddress"
                    placeholder="Linux"
                    className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                  />
                  {errors.ip_address && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.ip_address.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Device username and password inputs */}
              <div className="flex items-center">
                <div className="p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="deviceUsername"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device Username
                  </label>
                  <input
                    {...register("device_username")}
                    type="text"
                    id="deviceUsername"
                    placeholder="User1"
                    className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                  />
                </div>

                <div className="p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="devicePassword"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device Password
                  </label>
                  <div className="relative flex items-center">
                    <input
                      {...register("device_password")}
                      type={showPassword ? "text" : "password"}
                      id="devicePassword"
                      placeholder="**************"
                      className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2"
                    >
                      {!showPassword ? (
                        <Eye className="text-custom-bluenew" />
                      ) : (
                        <EyeOff className="text-custom-bluenew" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Device OS type and CPU type inputs */}
              <div className="flex items-center">
                <div className="relative p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="deviceOsType"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device OS Type<span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <select
                      {...register("device_os_type")}
                      id="deviceOsType"
                      className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                    >
                      <option value="">Select Device OS Type</option>
                      <option value="Windows">Windows</option>
                      <option value="Linux">Linux</option>
                    </select>
                    <ChevronDown className="absolute right-[3px] bg-white text-custom-bluenew" />
                  </div>
                  {errors.device_os_type && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.device_os_type.message}
                    </p>
                  )}
                </div>
                <div className="relative p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="deviceCpuType"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Device CPU Type<span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <select
                      {...register("device_cpu_type")}
                      id="deviceCpuType"
                      className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                    >
                      <option value="">Select Device CPU Type</option>
                      <option value="x86">x86</option>
                      <option value="AMD">AMD</option>
                      <option value="Power Pc">Power PC</option>
                    </select>
                    <ChevronDown className="absolute right-[3px] bg-white text-custom-bluenew" />
                  </div>
                  {errors.device_cpu_type && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.device_cpu_type.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Sensor Agent Type and button */}
              <div className="flex items-center">
                <div className="relative p-[10px] space-y-[10px] w-1/2">
                  <label
                    htmlFor="sensorAgentType"
                    className="font-bold text-sm text-custom-bluenew"
                  >
                    Sensor Agent Type<span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <select
                      {...register("sensor_agent_type")}
                      id="sensorAgentType"
                      className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew"
                    >
                      <option value="">Select Sensor Agent Type</option>
                      <option value="Agent">Agent</option>
                      <option value="Host">Host</option>
                    </select>
                    <ChevronDown className="absolute right-[3px] bg-white text-custom-bluenew" />
                  </div>
                  {errors.sensor_agent_type && (
                    <p className="text-red-500 absolute text-xs -bottom-2 left-3 font-semibold">
                      {errors.sensor_agent_type.message}
                    </p>
                  )}
                </div>
                <div className="p-[10px] w-1/2 mt-8 flex items-center gap-4">
                  <select
                    {...register("sensor_agent_version")}
                    className="w-[240px] h-[40px] px-4 py-2 rounded-lg bg-custom-blue7 text-white outline-none cursor-pointer appearance-none relative"
                  >
                    <option
                      value=""
                      className="bg-white text-gray-800 font-semibold text-sm text-center"
                    >
                      Select Sensor Agent
                    </option>
                    {sensorAgents.map((agent) => (
                      <option
                        key={agent.sensor_agent_id}
                        // key={agent.id}
                        value={agent.sensor_agent_version}
                        // value={agent.version}
                        className="bg-white text-gray-800 text-sm "
                      >
                        {agent.sensor_agent_name}
                        {/* {agent.name} */}
                      </option>
                    ))}
                  </select>
                  {errors.sensor_agent_version && (
                    <p className="text-red-500 text-xs left-[280px] top-[30%] font-semibold">
                      {errors.sensor_agent_version.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="text-center space-x-20 pt-7">
              <button
                type="button"
                className="px-6 h-[40px] bg-custom-primaryBlue text-white rounded-lg"
              >
                Validate and Deploy
              </button>
              <button
                type="submit"
                className="px-6 h-[40px] bg-custom-primaryBlue text-white rounded-lg"
              >
                Save
              </button>
              <button
                onClick={onClose}
                type="button"
                className="px-6 h-[40px] bg-custom-bluenew text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddNewDevice;
